/* istanbul ignore file */
/* tslint:disable */
 
import type { ECecRequestLog } from '../models/ECecRequestLog';
import type { ECecRequestLogRetry } from '../models/ECecRequestLogRetry';
import type { OneCRequestLog } from '../models/OneCRequestLog';
import type { OneCRequestLogRetry } from '../models/OneCRequestLogRetry';
import type { PaginatedECecRequestLogListList } from '../models/PaginatedECecRequestLogListList';
import type { PaginatedOneCRequestLogListList } from '../models/PaginatedOneCRequestLogListList';
import type { Py1CPropertyType } from '../models/Py1CPropertyType';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class IntegrationsService {

  /**
   * 1С property types helper.
   * @returns Py1CPropertyType 
   * @throws ApiError
   */
  public static integrations1CPropertyTypesList(): CancelablePromise<Array<Py1CPropertyType>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/integrations/1c/property-types/',
    });
  }

  /**
   * Admin-only ViewSet for managing 1C API request logs.
 *
 * Provides:
 * - List: View all request logs with filtering
 * - Retrieve: View a single log entry with full details
 * - Retry: Retry a failed request (custom action)
   * @param caller 
   * @param createdGte 
   * @param createdLte 
   * @param endpoint 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param isOriginal Filter to show only original logs (not retry attempts)
   * @param method 
   * @param nextRetryAtGte Logs with next auto-retry scheduled at or after this datetime (ISO 8601)
   * @param nextRetryAtIsnull True = not yet processed by auto-retry; False = scheduled or permanently skipped
   * @param nextRetryAtLte Logs with next auto-retry due at or before this datetime (ISO 8601)
   * @param order 
   * @param ordering Ordering fields: created, modified, status, caller, retry_count, next_retry_at
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param parentLog Filter by parent log ID (to find retry attempts)
   * @param resolvedByRetry Filter by whether the failed request was resolved by a retry
   * @param search Search fields: error_message, request_body, response_body, metadata
   * @param status * `success` - Success
 * * `error` - Error
 * * `network_error` - Network Error
   * @param unresolvedOnly Filter to show only unresolved errors (failed requests that have not been resolved by a retry)
   * @returns PaginatedOneCRequestLogListList 
   * @throws ApiError
   */
  public static integrations1CRequestLogsList(
caller?: string,
createdGte?: string,
createdLte?: string,
endpoint?: string,
excludeIds?: string,
ids?: string,
isOriginal?: boolean,
method?: string,
nextRetryAtGte?: string,
nextRetryAtIsnull?: boolean,
nextRetryAtLte?: string,
order?: number,
ordering?: string,
page?: number,
pageSize?: number,
parentLog?: number,
resolvedByRetry?: boolean,
search?: string,
status?: 'error' | 'network_error' | 'success',
unresolvedOnly?: boolean,
): CancelablePromise<PaginatedOneCRequestLogListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/integrations/1c/request-logs/',
      query: {
        'caller': caller,
        'created_gte': createdGte,
        'created_lte': createdLte,
        'endpoint': endpoint,
        'exclude_ids': excludeIds,
        'ids': ids,
        'is_original': isOriginal,
        'method': method,
        'next_retry_at_gte': nextRetryAtGte,
        'next_retry_at_isnull': nextRetryAtIsnull,
        'next_retry_at_lte': nextRetryAtLte,
        'order': order,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'parent_log': parentLog,
        'resolved_by_retry': resolvedByRetry,
        'search': search,
        'status': status,
        'unresolved_only': unresolvedOnly,
      },
    });
  }

  /**
   * Admin-only ViewSet for managing 1C API request logs.
 *
 * Provides:
 * - List: View all request logs with filtering
 * - Retrieve: View a single log entry with full details
 * - Retry: Retry a failed request (custom action)
   * @param id A unique integer value identifying this one c request log.
   * @returns OneCRequestLog 
   * @throws ApiError
   */
  public static integrations1CRequestLogsRetrieve(
id: number,
): CancelablePromise<OneCRequestLog> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/integrations/1c/request-logs/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Retry a failed 1C API request. Only POST requests with status 'error' or 'network_error' can be retried. Maximum retry attempts per log is 5.
   * @param id A unique integer value identifying this one c request log.
   * @param requestBody 
   * @returns OneCRequestLogRetry 
   * @throws ApiError
   */
  public static oneCRequestLogRetry(
id: number,
requestBody: OneCRequestLogRetry,
): CancelablePromise<OneCRequestLogRetry> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/integrations/1c/request-logs/{id}/retry/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param caller 
   * @param createdGte 
   * @param createdLte 
   * @param endpoint 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param method 
   * @param ordering Ordering fields: created, modified, status, caller, retry_count
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param parentLog Filter by parent log ID (to find retry attempts)
   * @param resolvedByRetry Filter by whether the failed request was resolved by a retry
   * @param search Search fields: error_message, request_body, response_body, metadata
   * @param status * `success` - Success
 * * `error` - Error
 * * `network_error` - Network Error
   * @param unresolvedOnly Show only unresolved errors (failed pay requests not resolved by retry)
   * @returns PaginatedECecRequestLogListList 
   * @throws ApiError
   */
  public static integrationsECecRequestLogsList(
caller?: string,
createdGte?: string,
createdLte?: string,
endpoint?: string,
excludeIds?: string,
ids?: string,
method?: string,
ordering?: string,
page?: number,
pageSize?: number,
parentLog?: number,
resolvedByRetry?: boolean,
search?: string,
status?: 'error' | 'network_error' | 'success',
unresolvedOnly?: boolean,
): CancelablePromise<PaginatedECecRequestLogListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/integrations/e-cec/request-logs/',
      query: {
        'caller': caller,
        'created_gte': createdGte,
        'created_lte': createdLte,
        'endpoint': endpoint,
        'exclude_ids': excludeIds,
        'ids': ids,
        'method': method,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'parent_log': parentLog,
        'resolved_by_retry': resolvedByRetry,
        'search': search,
        'status': status,
        'unresolved_only': unresolvedOnly,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this e cec request log.
   * @returns ECecRequestLog 
   * @throws ApiError
   */
  public static integrationsECecRequestLogsRetrieve(
id: number,
): CancelablePromise<ECecRequestLog> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/integrations/e-cec/request-logs/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Retry a failed E-CEC pay request. Only 'pay' requests with status 'error' or 'network_error' can be retried. Retry is only allowed on the same calendar day as the original request. Maximum retry attempts per log is 5.
   * @param id A unique integer value identifying this e cec request log.
   * @param requestBody 
   * @returns ECecRequestLogRetry 
   * @throws ApiError
   */
  public static eCecRequestLogRetry(
id: number,
requestBody: ECecRequestLogRetry,
): CancelablePromise<ECecRequestLogRetry> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/integrations/e-cec/request-logs/{id}/retry/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

}
